import { BasePatientDetailsFormFields, FileAccordionData, FileFormFields, FileMapEntry, PatientFileFormFields, Resource, StepperPatientFormFields, VendorApplicationCustomerResource } from 'common';
import { FullScreenImageDialogComponent } from '../../../full-screen-image-dialog.component';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FileService } from '../../../../../services/file.service';
import { PdfComponent } from '../../../../pdf/pdf.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { FormControl, FormGroup } from '@angular/forms';
import { ConfirmDialog } from '../../../confirm.dialog';
import { MatDialog } from '@angular/material/dialog';
import { NgClass, NgIf } from '@angular/common';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'lib-file-group',
  standalone: true,
  imports: [NgIf, NgClass, MatExpansionModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, PdfComponent, ConfirmDialog],
  templateUrl: './file-group.component.html',
  styleUrl: './file-group.component.scss',
  animations: [
    trigger(
      'fadeInOut',
      [
        transition(
          ':enter', [
          style({ opacity: 0 }),
          animate('0.3s ease-out',
            style({ opacity: 1 }))
        ]),
        transition(
          ':leave', [
          style({ opacity: 1 }),
          animate('0.3s ease-in',
            style({ opacity: 0 }))
        ])
      ]
    )
  ]
})
export class FileGroupComponent implements OnDestroy, OnInit {

  //#region Variables
  // Variables for this component that are within scope of "this" component.

  /**
   * Whether the component is in read mode.
   */
  @Input({ required: true })
  public isReadMode = false;

  /**
   * The form for the patient in this group.
   * 
   * Contains:
   * - photoIDBook
   * - photoPatientCard
   * - photoLabOrder
   * - misc files
   */
  @Input({ required: true })
  public baseForm!: FormGroup<BasePatientDetailsFormFields | null>;

  /**
   * The form group containing the file form fields.
   * This would only be for the customer, as it contains only the payslip fields.
   * 
   * Contains:
   * - payslip
   */
  @Input()
  public fileForms: FormGroup<FileFormFields | null> | null = null;

  /**
   * The accordion setup data.
   * This is iterated over to create the accordion items.
   * 
   * @see FileAccordionData @see constants.ts
   */
  @Input({ required: true })
  public accordionSetup: FileAccordionData[] = [];

  /**
   * The patient form for the stepper.
   * This will indicate what files are valid and invalid.
   *
   * @type {(FormGroup<StepperPatientFormFields> | undefined)}
   * @memberof FileGroupComponent
   */
  @Input({ required: true })
  public stepperPatientForm: FormGroup<StepperPatientFormFields> | undefined;


  /**
   * The file map containing the files for each accordion item.
   */
  public fileMap: Record<keyof FileFormFields | keyof PatientFileFormFields, FileMapEntry[]> = {} as any;

  //#endregion

  private fileService = inject(FileService)
  public snackbarService = inject(MatSnackBar);
  public fullScreenDialog = inject(MatDialog);
  private sanitizer = inject(DomSanitizer);
  private dialog = inject(MatDialog);

  //#region Instantiation
  // Setup\fetches\initialization.

  public async ngOnInit(): Promise<void> {

    // Setup the FileMap object based on the form values.
    // First load the initial values, if there are files this is where the "isLoadingFromServer" flag will be set while the next step fetches the files.
    let hasFiles = false;
    this.accordionSetup.forEach(async accord => {
      if (accord.formControlNameId && accord.formControlNameResource) {
        const { controlResource, controlId } = this.getControlPair(accord);

        if (controlId && controlResource && controlResource.value && controlId.value) {
          hasFiles = true; // Indicate that there are files to load.

          if (controlResource.value instanceof Array) {
            const parsedArray = (await this.toFileMapEntryArray(controlResource.value, false))
              .map(c => {
                if (c) c.isLoadingFromServer = true;
                return c;
              });
            this.fileMap[accord.formControlNameResource] = parsedArray;
          } else {
            const parsed = await this.toFileMapEntry(controlResource.value);
            parsed.id = controlId.value as number;
            parsed.isLoadingFromServer = true;
            this.fileMap[accord.formControlNameResource] = [parsed];
          }
        }
        // When the accordion is for vendorApplicationCustomerResources, the controlResource will be null while the controlId is not.
        else if (controlId && controlId && controlId.value) {
          hasFiles = true; // Indicate that there are files to load.
          /**
           * ControlID is in this case an array of VendorApplicationCustomerResource.
           * @see VendorApplicationCustomerResource
           */
          const currentValues = (<any>controlId.value) as VendorApplicationCustomerResource | VendorApplicationCustomerResource[];

          // instanceof check for future proofing.
          if (currentValues instanceof Array) {
            const parsedArray = (await this.toFileMapEntryArray(currentValues, false))
              .map(c => {
                if (c) c.isLoadingFromServer = true;
                return c;
              });
            // TODO: the IDs might be undefined...
            // console.log('parsedArray', parsedArray);
            this.fileMap[accord.formControlNameId] = parsedArray
          }
          // Future proofing for single values.
          else {
            const parsed = await this.toFileMapEntry(currentValues)
            parsed.id = controlId.value as number;
            parsed.isLoadingFromServer = true;
            this.fileMap[accord.formControlNameId] = [parsed];
          }
        }
      }
    });
    // if (!this.fileForms) console.log('file map sync:', this.fileMap);

    // Load the initial values.
    if (hasFiles) {
      this.accordionSetup.forEach(async accord => {
        if (accord.formControlNameId && accord.formControlNameResource) {
          const { controlResource, controlId } = this.getControlPair(accord);

          if (controlId && controlResource && controlResource.value && controlId.value) {

            if (controlResource.value instanceof Array) {
              const parsedArray = (await this.toFileMapEntryArray(controlResource.value, true))
                .map(c => {
                  if (c) c.isLoadingFromServer = false;
                  return c;
                });
              this.fileMap[accord.formControlNameResource] = parsedArray;
            } else {
              const parsed = await this.toFileMapEntryWithServerUrl(controlResource.value);
              parsed.id = controlId.value as number;
              parsed.isLoadingFromServer = false;
              this.fileMap[accord.formControlNameResource] = [parsed];
            }
          }
          // When the accordion is for vendorApplicationCustomerResources, the controlResource will be null while the controlId is not.
          else if (controlId && controlId && controlId.value) {
            /**
             * ControlID is in this case an array of VendorApplicationCustomerResource.
             * @see VendorApplicationCustomerResource
             */
            const currentValues = (<any>controlId.value) as VendorApplicationCustomerResource | VendorApplicationCustomerResource[];

            // instanceof check for future proofing.
            if (currentValues instanceof Array) {
              const parsedArray = (await this.toFileMapEntryArray(currentValues, true))
                .map(c => {
                  if (c) c.isLoadingFromServer = false;
                  return c;
                });
              // TODO: the IDs might be undefined...
              // console.log('parsedArray', parsedArray);
              this.fileMap[accord.formControlNameId] = parsedArray
            }
            // Future proofing for single values.
            else {
              const parsed = await this.toFileMapEntryWithServerUrl(currentValues);
              parsed.id = controlId.value as number;
              parsed.isLoadingFromServer = false;
              this.fileMap[accord.formControlNameId] = [parsed];
            }
          }

          // The below section is the "old" way of loading the file map.
          // This was replaced by the above code to handle Chrome automatically downloading PDFs.
          // The code remains for future reference.
          //// ------------------------------------------------------------------------------------------
          // if (controlId && controlResource && controlResource.value) {

          //   if (controlResource.value instanceof Array) {
          //     this.fileMap[accord.formControlNameResource] = controlResource.value.map((resource: Resource) => ({
          //       id: resource.id,
          //       resource: resource,
          //       isUploadedToServer: true,
          //       filename: resource.name,
          //       type: resource.contentType,
          //       serverUrl: resource.contentUrl
          //     }));
          //   } else {
          //     this.fileMap[accord.formControlNameResource] = [{
          //       id: (controlId.value as number || controlResource.value.id),
          //       resource: controlResource.value,
          //       isUploadedToServer: true,
          //       filename: controlResource.value.name,
          //       type: controlResource.value.contentType,
          //       serverUrl: controlResource.value.contentUrl
          //     }];
          //   }
          // }
          // // When the accordion is for vendorApplicationCustomerResources, the controlResource will be null while the controlId is not.
          // else if (controlId && controlId && controlId.value) {
          //   /**
          //    * ControlID is in this case an array of VendorApplicationCustomerResource.
          //    * @see VendorApplicationCustomerResource
          //    */
          //   const currentValues = (<any>controlId.value) as VendorApplicationCustomerResource | VendorApplicationCustomerResource[];

          //   // instanceof check for future proofing.
          //   if (currentValues instanceof Array) {
          //     this.fileMap[accord.formControlNameId] = currentValues.map(VCResource => ({
          //       id: VCResource.id,
          //       resource: VCResource.resource,
          //       isUploadedToServer: true,
          //       filename: VCResource.resource?.name || '',
          //       type: VCResource.resource?.contentType || '',
          //       serverUrl: VCResource.resource?.contentUrl
          //     }));
          //   }
          //   // Future proofing for single values.
          //   else {
          //     this.fileMap[accord.formControlNameId] = [{
          //       id: currentValues.id,
          //       resource: currentValues.resource,
          //       isUploadedToServer: true,
          //       filename: currentValues.resource?.name || '',
          //       type: currentValues.resource?.contentType || '',
          //       serverUrl: currentValues.resource?.contentUrl
          //     }];
          //   }
          // }
          //// ------------------------------------------------------------------------------------------
        }
        // if (!this.fileForms) console.log('file map async:', this.fileMap);
      });
    }
  }

  //#endregion
  //#region User Interactions
  // Callbacks from html, anything the user can "interact" with from the view.

  /**
   * Handles the file change event.
   *
   * @param {*} event The file change event from HTML.
   * @param {FileAccordionData} accord The accordion data for the file input.
   */
  public async onFileChange(event: any, accord: FileAccordionData): Promise<void> {
    if (!event.target.files || event.target.files.length < 1) return;
    this.fileForms?.markAsDirty();
    this.baseForm.markAsDirty();

    // If the control is not multiple and has a file, remove the previous file.
    if (!accord.multiple && this.fileMap[accord.fileMapKey]?.length > 0) {
      // Confirm replacement.
      if (await this.asyncConfirm('Replace file?', 'The existing file will be removed.')) await this.removeFile(0, accord, true);
      // Cancel the file upload if they say no.
      else return;
    }

    // Read file into local array.
    this.fileService.loadFilesFromEventToLocalArray(event.target.files, this.fileMap[accord.fileMapKey])
      .then((updatedFileList) => {
        this.fileMap[accord.fileMapKey] = updatedFileList;
        // Trigger upload for the new file(s),
        // And upload the file to the server.
        updatedFileList.filter(f => !f.isUploadedToServer)
          .forEach(async file => { await this.onUploadFile(file, accord); });
      })
      .catch(() => {
        this.snackbarService.open('Could not select one of the files. Please try again',
          'Dismiss', { duration: 5000 });
      });
  }

  /**
   * Uploads a file to the server.
   *
   * @param {FileMapEntry} file The file to upload.
   * @param {keyof FileFormFields | keyof PatientFileFormFields} controlName The name of the form control to update.
   */
  public onUploadFile(file: FileMapEntry, accord: FileAccordionData): void {
    this.fileService.uploadFileToServer(file)
      .then((updatedFile) => {
        const { controlResource, controlId } = this.getControlPair(accord);
        controlId?.markAsDirty();
        controlId?.markAsTouched();
        controlResource?.markAsDirty();
        controlResource?.markAsTouched();

        if (accord.multiple) {

          // if the control is vendorApplicationCustomerResources, then the shape of the file must be adjusted.
          if (accord.formControlNameId === 'vendorApplicationCustomerResources') {
            // "controlResource" will be set to null as it is not needed.

            if (controlId && updatedFile.resource) {
              /**
               * controlId.value === VendorApplicationCustomerResource[]
               * @see VendorApplicationCustomerResource
               */
              const currentValue = <VendorApplicationCustomerResource[]>((controlId.value as any[]) || []);
              // Drop any existing entry pointing at the same Resource so a re-upload or sync
              // can't create an in-memory duplicate that the next auto-save would persist.
              const deduped = currentValue.filter(r => r.resourceId !== updatedFile.resource!.id);
              deduped.push({
                id: null as any,
                vendorApplicationCustomerId: (this.baseForm?.value?.id || null) as any,
                resourceId: updatedFile.resource.id,
                resource: updatedFile.resource
              } as VendorApplicationCustomerResource);

              controlId.setValue(<any>deduped);
              controlId.updateValueAndValidity();
            }

          } else {
            // Add the file to the form control as an array.
            if (controlResource && updatedFile.resource) {
              const currentValue = <any[]>(controlResource.value || []);
              controlResource.setValue(<any>[...currentValue, updatedFile.resource]);
              controlResource.updateValueAndValidity();
            }

            if (controlId && updatedFile.resource) {
              const currentValue = <any[]>(controlId.value || []);
              controlId.setValue(<any>[...currentValue, updatedFile.resource.id]);
              controlId.updateValueAndValidity();
            }
          }

        } else {

          if (controlResource && updatedFile.resource) {
            controlResource.setValue(updatedFile.resource);
            controlResource.updateValueAndValidity();
          }

          if (controlId && updatedFile.resource) {
            controlId.setValue(updatedFile.resource.id);
            controlId.updateValueAndValidity();
          }
        }


        // Show a snackbar.
        this.snackbarService.open(accord.title + ' uploaded', 'Dismiss', { duration: 5000 });
      })
      .catch();
  }

  /**
   * Removes a file from the file map.
   *
   * @param {number} index The index of the file to remove.
   * @param {FileAccordionData} accord The accordion data for the file input.
   */
  public async removeFile(index: number, accord: FileAccordionData, bypassConfirm = false): Promise<void> {
    if (this.fileMap[accord.fileMapKey]
      && (bypassConfirm === true
        || (bypassConfirm === false && await this.asyncConfirm('Delete file?', 'The file will be removed.')))) {
      // TODO this will need to be replaced with actual file removal logic / API call.
      const removed = this.fileMap[accord.fileMapKey].splice(index, 1);

      // Remove local blob.
      removed?.forEach(f => {
        if (f.localUrl) URL.revokeObjectURL(f.localUrl);
      });

      // Update the form values.
      const { controlResource, controlId } = this.getControlPair(accord);

      // If the control is an array, use the updated fileMap to update the form controls and their values.
      if (accord.multiple && controlId) {

        // vendorApplicationCustomerResources is a special case.
        if (accord.formControlNameId === 'vendorApplicationCustomerResources') {
          /**
           * controlId.value === VendorApplicationCustomerResource[]
           * @see VendorApplicationCustomerResource
           */
          const currentValue = <VendorApplicationCustomerResource[]>((controlId.value as any[]) || []);
          controlId.setValue(this.fileMap[accord.fileMapKey]
            .filter(f => f.resource !== undefined)
            .map<VendorApplicationCustomerResource>(f => {
              const match = currentValue.find(c => c.resourceId === (f.resource as Resource).id);
              return {
                id: match?.id || null,
                vendorApplicationCustomerId: (match?.vendorApplicationCustomerId || this.baseForm?.value?.id || null),
                resourceId: (f.resource as Resource).id,
                resource: (f.resource as Resource)
              } as VendorApplicationCustomerResource
            }) as any[]); // As any to silence the type checker for angular forms.
          controlId.updateValueAndValidity();

        }
        // Future proofing for other arrays.
        else {
          if (controlResource) {
            controlResource.setValue(this.fileMap[accord.fileMapKey]
              .filter(f => f.resource !== undefined)
              .map<Resource>(f => (f.resource as Resource)));
            controlResource.updateValueAndValidity();
          }

          controlId.setValue(this.fileMap[accord.fileMapKey]
            .filter(f => f.resource !== undefined)
            .map<number>(f => (f.resource as Resource).id));
          controlId.updateValueAndValidity();
        }

      }
      // Else if single values, set the form control to null as the file has been removed.
      else {
        if (controlId) {
          controlId.setValue(null);
          controlId.updateValueAndValidity();
        }

        if (controlResource) {
          controlResource.setValue(null);
          controlResource.updateValueAndValidity();
        }
      }

      // Mark the form as dirty.
      this.fileForms?.markAsDirty();
      this.baseForm.markAsDirty();

      // Show a snackbar.
      this.snackbarService.open('File removed', 'Dismiss', { duration: 5000 });
    }
  }

  //#endregion
  //#region Events and Domain Functions
  // $watches/$on events, functions which don't fall into any of the other regions,
  // including any function that has to do domain functionality.

  public markAsTouched(accord: FileAccordionData) {
    const { controlResource, controlId } = this.getControlPair(accord);
    controlResource?.markAsTouched();
    controlId?.markAsTouched();

    if (this.fileForms && this.fileForms.controls) {
      this.fileForms.controls['photoPayslip'].markAsTouched();
      this.fileForms.controls['photoPayslipId'].markAsTouched();
    }
  }

  /**
   * Opens the file in full screen.
   *
   * @param {*} file The file to open.
   */
  public openFileInFullScreen(file: any): void {
    this.fullScreenDialog.open(FullScreenImageDialogComponent, {
      height: '95%',
      width: '95%',
      maxHeight: 'unset',
      maxWidth: 'unset',
      data: { file }
    });
  }

  //#endregion
  //#region Data Access & Subscriptions
  // API interactions, NGRX selects, etc.

  //#endregion
  //#region Setters, Updaters and Preloaders
  // Setter functions, and methods called from the Instantiation region.

  //#endregion
  //#region Getters and Filters
  // Getter functions and filter methods/functions.

  private getControlPair(accord: FileAccordionData): { controlResource: FormControl<Resource[] | Resource | null> | null; controlId: FormControl<number[] | number | null> | null; } {
    let controlId: FormControl<number | null> | null = null;
    let controlResource: FormControl<Resource[] | Resource | null> | null = null;

    // Is a payslip (customer) file.
    if ((accord.formControlNameId === "photoPayslipId" && accord.formControlNameResource === "photoPayslip") && this.fileForms !== null && this.fileForms.controls) {
      controlId = this.fileForms.controls[accord.formControlNameId];
      controlResource = this.fileForms.controls[accord.formControlNameResource];
    }

    // Is a patient file.
    else if (this.baseForm.controls !== null) {
      controlResource = this.baseForm.controls[accord.formControlNameResource as keyof PatientFileFormFields] as FormControl<Resource | null>;
      controlId = this.baseForm.controls[accord.formControlNameId as keyof PatientFileFormFields] as FormControl<number | null>;

      if (accord.formControlNameResource === 'vendorApplicationCustomerResources') {
        controlResource = null;
      }

    }

    return { controlResource, controlId };
  }

  //#endregion
  //#region Change Detection
  // Deep config comparisons, change log generation, hasChangesFn, etc.

  //#endregion    
  //#region Parsing and Validation
  // Transformation/mapping methods
  // Methods returning true/false: isCurrent, areFeedsEqual, shouldProcess, etc.

  public isAccordValid(accord: FileAccordionData) {
    if (accord.isRequired === false) return true;

    // const { controlResource, controlId } = this.getControlPair(accord);
    const controlId = this.stepperPatientForm?.controls[accord.formControlNameId as keyof StepperPatientFormFields] as FormControl<number | number[] | null>;
    const controlResource = this.stepperPatientForm?.controls[accord.formControlNameResource as keyof StepperPatientFormFields] as FormControl<Resource | Resource[] | null>;


    // vendorApplicationCustomerResources is a special case.
    if (accord.formControlNameId === 'vendorApplicationCustomerResources')
      return (controlId?.value || null) !== null;
    else
      return (controlResource?.value || null) !== null && (controlId?.value || null) !== null;
  }

  public isAccordTouched(accord: FileAccordionData) {
    // const { controlResource, controlId } = this.getControlPair(accord);
    const controlId = this.stepperPatientForm?.controls[accord.formControlNameId as keyof StepperPatientFormFields] as FormControl<number | number[] | null>;
    const controlResource = this.stepperPatientForm?.controls[accord.formControlNameResource as keyof StepperPatientFormFields] as FormControl<Resource | Resource[] | null>;

    // vendorApplicationCustomerResources is a special case.
    if (accord.formControlNameId === 'vendorApplicationCustomerResources')
      return (controlId?.touched || false);
    else return (controlResource?.touched || false) && (controlId?.touched || false);

  }

  private async toFileMapEntryArray(resources: (Resource | VendorApplicationCustomerResource)[], fetchServerUrl: boolean) {
    return await Promise.all(resources.map(async resource => {
      if (fetchServerUrl)
        return await this.toFileMapEntryWithServerUrl(resource);
      else
        return await this.toFileMapEntry(resource);
    }));
  }

  /**
   * Different browsers have different ways of handling file URLs.
   * Chrome will render img urls in html, but for PDF it will download it.
   * Firefox will render img and pdf urls in html.
   * Not sure about Edge, Opera, Safari.
   * 
   * This function will download the server URL and save it to a local blob.
   * @param resource The resource to download.
   */
  private async toFileMapEntryWithServerUrl(resource: Resource | VendorApplicationCustomerResource) {

    if (isVendorApplicationCustomerResource(resource)) {
      const localConvertedServerUrl = await this.fileService.convertServerUrlToLocalUrl(resource.resource?.contentUrl, resource.resource?.contentType as string);
      return this.toFileMapEntry(resource, localConvertedServerUrl);
    }

    else {
      const localConvertedServerUrl = await this.fileService.convertServerUrlToLocalUrl(resource?.contentUrl, resource.contentType);
      return this.toFileMapEntry(resource, localConvertedServerUrl);
    }
  }

  private toFileMapEntry(resource: Resource | VendorApplicationCustomerResource, localConvertedServerUrl?: string) {
    if (isVendorApplicationCustomerResource(resource))
      return {
        id: resource.id,
        resource: resource.resource,
        isUploadedToServer: resource.resource !== null && resource.resource !== undefined,
        filename: resource.resource?.name || '',
        type: resource.resource?.contentType || '',
        localUrl: localConvertedServerUrl,
        safeLocalUrl: localConvertedServerUrl ? this.sanitizer.bypassSecurityTrustUrl(localConvertedServerUrl || '') : undefined,
      } as FileMapEntry;

    else return {
      id: resource.id,
      resource: resource,
      isUploadedToServer: resource.contentUrl !== null && resource.contentUrl !== undefined,
      filename: resource.name,
      type: resource.contentType,
      localUrl: localConvertedServerUrl,
      safeLocalUrl: localConvertedServerUrl ? this.sanitizer.bypassSecurityTrustUrl(localConvertedServerUrl || '') : undefined,
    };
  }

  //#endregion
  //#region Response Handlers
  // API/NGRX callback handlers.

  //#endregion
  //#region Navigation
  // Confirmation close dialogs, router guards, etc.

  private asyncConfirm(title: string, description: string): Promise<boolean> {
    return firstValueFrom(this.dialog.open(ConfirmDialog, {
      data: {
        title: title,
        description: description,
        confirmResultData: true
      }
    }).afterClosed());
  }

  //#endregion
  //#region Post Instantiation
  // Post-init fetch requests, used in such as landing page, after view init, etc.

  //#endregion

  public ngOnDestroy(): void {

    // Revoke all object URLs.
    Object.values(this.fileMap).forEach(files => {
      files.forEach(f => {
        if (f.localUrl) URL.revokeObjectURL(f.localUrl);
        if (f.serverUrl) URL.revokeObjectURL(f.serverUrl);
      });
    });
  }

}

function isVendorApplicationCustomerResource(resource: Resource | VendorApplicationCustomerResource): resource is VendorApplicationCustomerResource {
  return (resource as VendorApplicationCustomerResource).resource !== undefined;
}

